import React, { useState, useRef, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/header/header';
import Main from './components/BODY/Highlight/Highlight'; // Keep Main eagerly loaded for LCP
import End from './components/BODY/END/end';
import Footer from './components/Footer/Footer';
import EntryAnimation from './components/entry_animation/EntryAnimation';
import SmoothScroll from './components/SmoothScroll';
import { scrollTo, getLenis } from './utils/lenis';

// Lazy Loaded Components for Maximum Performance
const Gallery = lazy(() => import('./components/BODY/Gallary/Gallery'));
const Integration = lazy(() => import('./components/BODY/Integration/Integration'));
const OurTeam = lazy(() => import('./components/BODY/Our_Team/our_team'));
const QN = lazy(() => import('./components/QN/QN'));
const OurProduct = lazy(() => import('./components/BODY/OurProducts/our_products'));
const AboutUs = lazy(() => import('./about/AboutUs'));
const Project = lazy(() => import('./project/Project'));

// Page-level motion config — opacity+y only (GPU-composited, zero flicker)
const pageTransition = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -16, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

function Home() {
  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <Main />
      <Suspense fallback={<div style={{ height: '100vh' }} />}>
        <Gallery />
        <Integration />
        <OurTeam />
        <OurProduct />
        <QN />
      </Suspense>
    </motion.div>
  );
}

export default function App() {
  const [showApp, setShowApp] = useState(false);
  const location = useLocation();
  const navigationType = useNavigationType();

  const prevPathRef = useRef(location.pathname);
  const transitionDataRef = useRef({ cameFromProject: false, isBackNav: false, targetPath: location.pathname });
  const scrollPositions = useRef({});

  if (prevPathRef.current !== location.pathname) {
    // Save scroll position of the page we are leaving
    scrollPositions.current[prevPathRef.current] = window.scrollY || 0;

    transitionDataRef.current = {
      cameFromProject: prevPathRef.current.startsWith('/project'),
      isBackNav: navigationType === 'POP',
      targetPath: location.pathname
    };
    prevPathRef.current = location.pathname;
  }

  return (
    <ThemeProvider>
      <EntryAnimation onFadeStart={() => setShowApp(true)} />
      {showApp && (
        <SmoothScroll>
          <Header />
          <div style={{ minHeight: '100vh' }}>
            <AnimatePresence
            mode="wait"
            onExitComplete={() => {
              const { cameFromProject, isBackNav, targetPath } = transitionDataRef.current;
              const savedScroll = (cameFromProject && isBackNav) ? (scrollPositions.current[targetPath] || 0) : 0;
              
              // Prevent native jump by setting it synchronously
              window.scrollTo({ top: savedScroll, behavior: 'instant' });

              // Sync Lenis instantly before the new page transition paints
              requestAnimationFrame(() => {
                const lenis = getLenis();
                if (lenis) {
                  // Force Lenis to recalculate the document height
                  lenis.resize();
                  lenis.scrollTo(savedScroll, { immediate: true });
                }
              });
            }}
          >
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={
                <Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <AboutUs />
                </Suspense>
              } />
              <Route path="/project/:id" element={
                <Suspense fallback={<div style={{ height: '100vh' }} />}>
                  <Project />
                </Suspense>
              } />
            </Routes>
          </AnimatePresence>
          </div>
          <End />
          <Footer />
        </SmoothScroll>
      )}
    </ThemeProvider>
  );
}